/**
 * Frontend entry for the shared client-communication dictionary.
 *
 * Re-exports everything from `supabase/functions/_shared/i18n` (the same
 * files the edge functions import under Deno) and adds the React glue:
 * `LanguageProvider` wraps a client-facing page with the language its RPC
 * payload resolved (`language` on get_proposal_public_view,
 * invoice_public_view, get_standard_portal_bootstrap/…_view, …), and
 * `useCustomerLanguage()` / `useT()` read it back anywhere below.
 *
 * Scope: only what the contractor's END CLIENT sees. The contractor-facing
 * app is English and never mounts a provider, so the default is 'en'.
 */
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  type Lang,
  type TranslationVars,
  DEFAULT_LANGUAGE,
  localeTag,
  normalizeLanguage,
  plural,
  t,
} from '../../supabase/functions/_shared/i18n/index';

export * from '../../supabase/functions/_shared/i18n/index';

const LanguageContext = createContext<Lang>(DEFAULT_LANGUAGE);

/**
 * `index.html` ships `<html lang="en">` and nothing ever changed it, so a
 * fully translated client page was still announced as English — a screen
 * reader read French copy with an English voice, and the browser offered to
 * "translate this page from English". The provider owns the attribute for as
 * long as a client surface is mounted and puts back exactly what it found on
 * unmount, so contractor screens (which never mount a provider) stay 'en'.
 *
 * The value is the full BCP-47 tag the page already formats its money and
 * dates with (`fr-CA`, not bare `fr`), and `dir` is pinned to 'ltr' — all
 * three languages are LTR; this is a declaration, not RTL support.
 */
function useDocumentLanguageAttribute(lang: Lang): void {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const el = document.documentElement;
    const previousLang = el.getAttribute('lang');
    const previousDir = el.getAttribute('dir');
    el.setAttribute('lang', localeTag(lang));
    el.setAttribute('dir', 'ltr');
    return () => {
      if (previousLang === null) el.removeAttribute('lang');
      else el.setAttribute('lang', previousLang);
      if (previousDir === null) el.removeAttribute('dir');
      else el.setAttribute('dir', previousDir);
    };
  }, [lang]);
}

export function LanguageProvider({
  language,
  children,
}: {
  /** Anything the payload gave us; NULL/unknown collapses to English. */
  language: string | null | undefined;
  children: ReactNode;
}) {
  const lang = normalizeLanguage(language);
  useDocumentLanguageAttribute(lang);
  return createElement(LanguageContext.Provider, { value: lang }, children);
}

/** The client language of the surface being rendered ('en' outside a provider). */
export function useCustomerLanguage(): Lang {
  return useContext(LanguageContext);
}

/** `t` and `plural` bound to the surrounding provider's language. */
export function useT() {
  const lang = useCustomerLanguage();
  const translate = useCallback((key: string, vars?: TranslationVars) => t(lang, key, vars), [lang]);
  const translatePlural = useCallback(
    (key: string, count: number, vars?: TranslationVars) => plural(lang, key, count, vars),
    [lang],
  );
  return { lang, t: translate, plural: translatePlural };
}

/** `'fr-CA'` → `'fr'`; anything unsupported collapses to English. */
export function languageFromTag(tag: string | null | undefined): Lang {
  return normalizeLanguage(typeof tag === 'string' ? tag.split('-')[0] : tag);
}

/**
 * The language currently declared on `<html>`, for the few components that
 * mount ABOVE every `LanguageProvider` and so cannot read the context — the
 * app-wide sonner `<Toaster>` in App.tsx is the only one today. Follows the
 * attribute the provider writes, so a toast fired from a French client page
 * announces itself in French while the contractor app stays English.
 */
export function useDocumentLanguage(): Lang {
  const read = () =>
    typeof document === 'undefined'
      ? DEFAULT_LANGUAGE
      : languageFromTag(document.documentElement.getAttribute('lang'));
  const [lang, setLang] = useState<Lang>(read);
  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
    const el = document.documentElement;
    const sync = () => setLang(languageFromTag(el.getAttribute('lang')));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(el, { attributes: true, attributeFilter: ['lang'] });
    return () => observer.disconnect();
  }, []);
  return lang;
}
